import type { ApplicantSectionV2 } from "@credit-acceptance/step6-bff-client";
import { toast } from "@credit-acceptance/uikit";
import { Zap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useReadOnly } from "../lock/ReadOnlyContext";
import ViewBureauModal from "../viewBureau/ViewBureauModal";
import { isRunnableActionId } from "./actionRegistry";
import { downloadDealSummaryPdf } from "./api/downloadDealSummaryPdf";
import { openMailContract } from "./api/openMailContract";
import { printFundingFax } from "./api/printFundingFax";
import type { WorkbenchAction } from "./api/useWorkbenchActions";
import FabPopover from "./FabPopover";

export const ACTIONS_PANEL_ID = "actions-panel";

type ActionsPopoverProperties = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
  /** Driver applicant section from the dashboard response, forwarded to the View Bureau modal. */
  driver: ApplicantSectionV2 | undefined;
  /** 2nd signer applicant section, forwarded to the View Bureau modal. */
  secondSigner: ApplicantSectionV2 | undefined;
  /** Role-scoped actions for the popover; those without a handler render disabled. */
  actions: WorkbenchAction[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

function ActionsPopover({
  open,
  onOpenChange,
  approvalId,
  driver,
  secondSigner,
  actions,
  isLoading,
  isError,
  onRetry,
}: ActionsPopoverProperties) {
  const { t } = useTranslation();
  const [viewBureauOpen, setViewBureauOpen] = useState(false);
  const readOnly = useReadOnly();

  const handleActionClick = async (actionId: string) => {
    onOpenChange(false);

    if (!isRunnableActionId(actionId)) {
      return;
    }

    switch (actionId) {
      case "view-bureau": {
        setViewBureauOpen(true);
        break;
      }
      case "print-step5-deal-summary": {
        downloadDealSummaryPdf(approvalId);
        break;
      }
      case "print-funding-fax": {
        printFundingFax(approvalId);
        break;
      }
      case "mail-contract": {
        try {
          await openMailContract(approvalId);
        } catch {
          toast.error(t("actions.mailContractError"));
        }
        break;
      }
    }
  };

  return (
    <>
      <FabPopover
        label={t("actions.menu")}
        icon={<Zap className="size-6" />}
        variant="navy"
        panelId={ACTIONS_PANEL_ID}
        open={open}
        onOpenChange={onOpenChange}
        disabled={readOnly}
        contentClassName="w-56 p-1"
      >
        {isLoading ? (
          <p role="status" className="text-muted-foreground px-3 py-2 text-sm">
            {t("actions.loading")}
          </p>
        ) : isError ? (
          <div role="alert" className="flex flex-col gap-2 px-3 py-2 text-sm">
            <span>{t("actions.loadError")}</span>
            <button type="button" className="text-left font-medium underline" onClick={onRetry}>
              {t("actions.retry")}
            </button>
          </div>
        ) : actions.length === 0 ? (
          <p className="text-muted-foreground px-3 py-2 text-sm">{t("actions.empty")}</p>
        ) : (
          <ul role="menu" aria-label={t("actions.menu")}>
            {actions.map((item) => (
              <li key={item.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={!isRunnableActionId(item.id)}
                  title={isRunnableActionId(item.id) ? undefined : t("actions.comingSoon")}
                  className="hover:bg-accent disabled:text-muted-foreground focus-visible:bg-accent text-foreground w-full rounded-sm px-3 py-2 text-left text-sm outline-none transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  onClick={() => void handleActionClick(item.id)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </FabPopover>
      <ViewBureauModal
        open={viewBureauOpen}
        onOpenChange={setViewBureauOpen}
        approvalId={approvalId}
        driver={driver}
        secondSigner={secondSigner}
      />
    </>
  );
}

export default ActionsPopover;
